import React from 'react';
import { useContext, useEffect, useState } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';
import GatingModal from '../components/GatingModal';
import { AuthContext } from '../context/AuthContext';
import { Button, Card, EmptyState, Skeleton, SkeletonGroup, Modal } from '../components/ui';

const FALLBACK_CHECKOUT_PLANS = [
  { planId: 'free', name: 'Free Plan', priceCents: 0, tier: 'free', checkout: null },
  { planId: 'creator', name: 'Creator', priceCents: 900, tier: 'creator', checkout: 'subscription' },
  { planId: 'pro', name: 'Pro', priceCents: 2900, tier: 'pro', checkout: 'subscription' },
  { planId: 'enterprise', name: 'Enterprise', priceCents: 0, tier: 'enterprise', checkout: null },
];

function checkoutPrice(plan) {
  const cents = Number(plan.priceCents) || 0;
  const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
  if (plan.checkout === 'subscription') return `$${dollars}/mo`;
  if (plan.tier === 'enterprise') return 'Custom';
  return 'Included';
}

export default function ProfileHub() {
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState({ name: '', avatarUrl: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [confirmState, setConfirmState] = useState(null);
  // Embedded: recent uploads and streams
  const [uploads, setUploads] = useState({ items: [], loading: true, error: '', page: 1, hasMore: false });
  const [streams, setStreams] = useState({ items: [], loading: true, error: '' });
  const [streamDurations, setStreamDurations] = useState({}); // id->seconds
  // Appearance
  const [themeChoice, setThemeChoice] = useState(() => {
    try { return localStorage.getItem('theme_choice') || 'dark'; } catch { return 'dark'; }
  });
  const [gateOpen, setGateOpen] = useState(false);
  const [projectPicker, setProjectPicker] = useState({ open: false, items: [], selectedId: '' });
  const [creating, setCreating] = useState(false);
  const [routes, setRoutes] = useState({ items: [], loading: false, selectedId: '' });
  const [adsStats, setAdsStats] = useState({ ok: false, totals: { total: 0 }, last7Days: [], loading: false });
  const [checkoutPlans, setCheckoutPlans] = useState(FALLBACK_CHECKOUT_PLANS);

  useEffect(() => {
    let alive = true;
    // Profile
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const me = await api.me();
        const item = me?.item || me || {};
        if (alive) setProfile({ name: item.name || '', avatarUrl: item.avatarUrl || '' });
      } catch (e) {
        if (alive) setError('Failed to load profile');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // Ads Analytics (admin-only; gracefully ignore errors)
    (async () => {
      try {
        setAdsStats(s => ({ ...s, loading: true }));
        const r = await api.getAdsAnalytics();
        if (r?.ok) setAdsStats({ ok: true, totals: r.totals || { total: 0 }, last7Days: Array.isArray(r.last7Days) ? r.last7Days : [], loading: false });
        else setAdsStats({ ok: false, totals: { total: 0 }, last7Days: [], loading: false });
      } catch {
        setAdsStats({ ok: false, totals: { total: 0 }, last7Days: [], loading: false });
      }
    })();
    // Recent uploads (paged, 5 per page, fetch 6 to detect more)
    const fetchUploads = async (page = 1) => {
      try {
        const res = await api.listMyUploads({ page, limit: 6, sort: 'createdAt:desc' });
        const raw = Array.isArray(res?.items) ? res.items : [];
        const hasMore = raw.length >= 6;
        const items = hasMore ? raw.slice(0, 5) : raw;
        if (alive) setUploads({ items, loading: false, error: '', page, hasMore });
      } catch (e) {
        if (alive) setUploads({ items: [], loading: false, error: 'Failed to load uploads', page, hasMore: false });
      }
    };
    (async () => {
      try {
        const r = await api.listCheckoutPlans();
        if (Array.isArray(r?.plans) && r.plans.length) setCheckoutPlans(r.plans);
      } catch {
        // The fallback list still offers Creator and Pro.
      }
    })();
    fetchUploads(1);
    // Recent streams (limit 5)
    (async () => {
      try {
        const res = await api.listStreams();
        const items = Array.isArray(res?.items) ? res.items.slice(0, 5) : [];
        if (alive) setStreams({ items, loading: false, error: '' });
        // Probe durations asynchronously
        setTimeout(() => {
          items.forEach((s) => {
            try {
              const rel = typeof s.audioPath === 'string' ? s.audioPath : '';
              const audioUrl = rel && (rel.startsWith('http://') || rel.startsWith('https://')) ? rel : (rel ? `${API_BASE}${rel}` : '');
              if (!audioUrl) return;
              const key = s._id || s.createdAt || audioUrl;
              const a = new Audio();
              a.crossOrigin = 'anonymous';
              a.preload = 'metadata';
              a.src = audioUrl;
              a.addEventListener('loadedmetadata', () => {
                const d = a.duration;
                if (Number.isFinite(d)) {
                  setStreamDurations((prev) => ({ ...prev, [key]: d }));
                }
                a.src = '';
              });
            } catch {}
          });
        }, 0);
      } catch (e) {
        if (alive) setStreams({ items: [], loading: false, error: 'Failed to load streams' });
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load routes when a project is selected in picker
  useEffect(() => {
    (async () => {
      try {
        if (!projectPicker.open || !projectPicker.selectedId) return;
        setRoutes((r) => ({ ...r, loading: true }));
        const res = await api.getProjectRoutes(projectPicker.selectedId);
        const items = Array.isArray(res?.items) ? res.items : [];
        setRoutes({ items, loading: false, selectedId: '' });
      } catch {
        setRoutes({ items: [], loading: false, selectedId: '' });
      }
    })();
  }, [projectPicker.open, projectPicker.selectedId]);

  async function onCreateVideo() {
    try {
      const cr = await api.getMyCredits();
      const balance = Number(cr?.balance ?? 0);
      const need = 24;
      if (Number.isFinite(balance) && balance < need) {
        setGateOpen(true);
        return;
      }
      const lp = await api.listProjects();
      const items = Array.isArray(lp?.items) ? lp.items : [];
      if (!items.length) { toast({ type: 'info', message: 'Create a project first' }); return; }
      if (items.length > 1 && !projectPicker.open) {
        setProjectPicker({ open: true, items, selectedId: items[0]?.id || '' });
        return;
      }
      const projectId = projectPicker.open ? projectPicker.selectedId || items[0].id : items[0].id;
      setCreating(true);
      const chosenRoute = routes.items.find(r => r?.id === routes.selectedId);
      const payload = chosenRoute?.id ? { routeId: chosenRoute.id } : {};
      const res = await api.createProjectVideo(projectId, payload);
      setCreating(false);
      if (res?.error) { toast({ type: 'error', message: res.error || 'Failed to create video' }); return; }
      const sid = res?.item?._id || res?.streamId;
      const qs = sid ? `projectId=${encodeURIComponent(projectId)}&streamId=${encodeURIComponent(sid)}` : `projectId=${encodeURIComponent(projectId)}`;
      window.location.href = `/adstudio?${qs}`;
    } catch (e) {
      setCreating(false);
      toast({ type: 'error', message: 'Failed to start video' });
    }
  }

  // Apply theme according to choice (system/light/dark)
  useEffect(() => {
    try { localStorage.setItem('theme_choice', themeChoice); } catch {}
    const apply = () => {
      if (themeChoice === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
        return;
      }
      if (themeChoice === 'light') {
        document.documentElement.setAttribute('data-theme', 'light');
        return;
      }
      // system
      // NOTE: bare :root in styles/tokens.css is the DARK palette (light only
      // applies under the explicit [data-theme="light"] selector), so
      // removing the attribute here does NOT produce a light theme — it
      // falls back to dark. This is a known, unresolved bug affecting
      // "system" users whose OS prefers light. Also note the light theme
      // itself is incomplete (several tokens like --gradient-hero and the
      // hardcoded `color-scheme: dark` in theme.css have no light override),
      // so this needs a product/design decision before fixing, not a
      // one-line patch here.
      const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
      const prefersDark = !!(mq && mq.matches);
      if (prefersDark) document.documentElement.setAttribute('data-theme', 'dark');
      else document.documentElement.removeAttribute('data-theme');
    };
    apply();
    if (themeChoice === 'system' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = () => apply();
      mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
      return () => {
        mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
      };
    }
    return undefined;
  }, [themeChoice]);

  function deleteUpload(name) {
    if (!name) return;
    setConfirmState({
      title: 'Delete upload',
      message: `Delete ${name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          const r = await api.deleteMyUpload(name);
          if (r?.error) throw new Error(r.error);
          // reload current page
          const { page } = uploads;
          setUploads((u) => ({ ...u, loading: true }));
          try {
            const res = await api.listMyUploads({ page, limit: 6, sort: 'createdAt:desc' });
            const raw = Array.isArray(res?.items) ? res.items : [];
            const hasMore = raw.length >= 6;
            const items = hasMore ? raw.slice(0, 5) : raw;
            setUploads({ items, loading: false, error: '', page, hasMore });
          } catch (e) {
            setUploads({ items: [], loading: false, error: 'Failed to load uploads', page, hasMore: false });
          }
        } catch (e) {
          toast({ type: 'error', message: e?.message || 'Delete failed' });
        }
      },
    });
  }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');
    try {
      const res = await api.updateMe({ name: profile.name, avatarUrl: profile.avatarUrl });
      if (res?.error) throw new Error(res.error);
      setSuccess('Profile updated');
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  function onDelete() {
    setConfirmState({
      title: 'Delete account',
      message: 'Delete your account? This cannot be undone.',
      confirmLabel: 'Delete account',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          const res = await api.deleteMe();
          if (res?.error) throw new Error(res.error);
          logout();
        } catch (e) {
          toast({ type: 'error', message: e.message || 'Delete failed' });
        }
      },
    });
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ color: '#ff7a45', marginBottom: 24 }}>Profile</h2>
      
      {loading ? (
        <div style={{ display: 'grid', gap: 16, maxWidth: 600 }}>
          <Skeleton variant="card" height="200px" />
          <Skeleton variant="card" height="150px" />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, maxWidth: 600 }}>
          <Card title="Account Information">
            <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
              <div>
                <span style={{ color: '#9ca3af', fontSize: 14 }}>Email</span>
                <p style={{ margin: '4px 0 0 0', color: '#e5e7eb', fontSize: 16 }}>{user?.email || '—'}</p>
              </div>
              <div>
                <span style={{ color: '#9ca3af', fontSize: 14 }}>User ID</span>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14, fontFamily: 'monospace' }}>{user?._id || '—'}</p>
              </div>
            </div>
            
            <form onSubmit={onSave} style={{ display: 'grid', gap: 16 }}>
              <div>
                <label htmlFor="pf-name" style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#d1d5db', marginBottom: 6 }}>
                  Display Name
                </label>
                <input 
                  id="pf-name" 
                  name="name" 
                  value={profile.name} 
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })} 
                  placeholder="Your name"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 8, 
                    border: '1px solid #374151', 
                    background: '#0b0b0e', 
                    color: '#e5e7eb',
                    fontSize: 16,
                    outline: 'none',
                    transition: 'all 0.2s'
                  }} 
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff5a1f';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 90, 31, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#374151';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label htmlFor="pf-avatar" style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#d1d5db', marginBottom: 6 }}>
                  Avatar URL
                </label>
                <input 
                  id="pf-avatar" 
                  name="avatarUrl" 
                  value={profile.avatarUrl} 
                  onChange={(e) => setProfile({ ...profile, avatarUrl: e.target.value })} 
                  placeholder="https://example.com/avatar.jpg"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    borderRadius: 8, 
                    border: '1px solid #374151', 
                    background: '#0b0b0e', 
                    color: '#e5e7eb',
                    fontSize: 16,
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff5a1f';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 90, 31, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#374151';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button type="submit" loading={saving} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button type="button" onClick={onDelete} variant="danger">
                  Delete Account
                </Button>
                {success && <span style={{ color: '#34d399', fontSize: 14 }}>✓ {success}</span>}
              </div>
              {error && <div style={{ color: '#fca5a5', fontSize: 14 }}>{error}</div>}
            </form>
          </Card>

          {/* Billing / Upgrade */}
          <section className="section" style={{ maxWidth: 520, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Billing</h3>
            <p style={{ color: '#9ca3af', marginTop: 4 }}>Upgrade your plan via secure checkout.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {checkoutPlans.filter((plan) => !['voice_ad', 'video_ad'].includes(plan.planId) && !['ad', 'video'].includes(plan.tier)).map((plan) => (
                plan.checkout === 'subscription' ? (
                  <button key={plan.planId} type="button" onClick={async () => {
                    try {
                      const r = await api.startCheckout(plan.planId);
                      if (r?.url) { window.location.href = r.url; return; }
                      toast({ type: 'error', message: r?.message || 'Failed to start checkout' });
                    } catch (e) { toast({ type: 'error', message: 'Checkout error' }); }
                  }} style={{ padding: '8px 12px', background: '#ff5a1f', color: '#fff', border: 0, borderRadius: 8 }}>{plan.name} · {checkoutPrice(plan)}</button>
                ) : (
                  <span key={plan.planId} style={{ color: '#9ca3af', fontSize: 14 }}>{plan.name} · {checkoutPrice(plan)}</span>
                )
              ))}
            </div>
          </section>

          {/* Appearance settings */}
          <section className="section" style={{ maxWidth: 520, marginTop: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Appearance</h3>
            <p style={{ color: '#9ca3af', marginTop: 4 }}>Choose your theme preference.</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
              <label htmlFor="theme-choice" style={{ minWidth: 84 }}>Theme</label>
              <select id="theme-choice" value={themeChoice} onChange={(e) => setThemeChoice(e.target.value)} className="select" style={{ maxWidth: 200 }}>
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
          </section>

          {/* Creations */}
          <section style={{ marginTop: 24 }}>
            <h3 style={{ color: '#e5e7eb' }}>Creations</h3>
            <p style={{ color: '#9ca3af', marginTop: 4 }}>Create and manage your content.</p>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <a href="/stream" style={cardLink}>Stream Creator →</a>
              <a href="/adstudio" style={cardLink}>Ad Studio →</a>
              <a href="/amplify" style={cardLink}>Amplify →</a>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button onClick={onCreateVideo}>{creating ? 'Starting…' : 'Create New Video'}</Button>
              {projectPicker.open && (
                <>
                  <span style={{ color: '#9ca3af' }}>Project:</span>
                  <select
                    value={projectPicker.selectedId}
                    onChange={(e)=>setProjectPicker(p=>({ ...p, selectedId: e.target.value }))}
                    className="select"
                    style={{ minWidth: 220 }}
                  >
                    {projectPicker.items.map(p => (
                      <option key={p.id} value={p.id}>{p.name || p.id}</option>
                    ))}
                  </select>
                  <span style={{ color: '#9ca3af' }}>Route:</span>
                  <select
                    value={routes.selectedId}
                    onChange={(e)=>setRoutes(r=>({ ...r, selectedId: e.target.value }))}
                    className="select"
                    style={{ minWidth: 220 }}
                  >
                    <option value="">(latest or none)</option>
                    {routes.items.map(rt => (
                      <option key={rt.id} value={rt.id}>{rt.meta?.tone || rt.id}</option>
                    ))}
                  </select>
                  <Button onClick={onCreateVideo}>Continue</Button>
                </>
              )}
            </div>
            <div style={{ marginTop: 16, background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 12 }}>
              <h4 style={{ marginTop: 0, color: '#e5e7eb' }}>Recent Streams</h4>
              {streams.loading ? (
                <div style={{ color: '#9ca3af' }}>Loading…</div>
              ) : streams.error ? (
                <div style={{ color: '#fca5a5' }}>{streams.error}</div>
              ) : streams.items.length === 0 ? (
                <div style={{ color: '#9ca3af' }}>No streams yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {streams.items.map(s => {
                    const rel = typeof s.audioPath === 'string' ? s.audioPath : '';
                    const audioUrl = rel && (rel.startsWith('http://') || rel.startsWith('https://')) ? rel : (rel ? `${API_BASE}${rel}` : '');
                    const key = s._id || s.createdAt || audioUrl;
                    const dur = streamDurations[key];
                    const created = s.createdAt ? new Date(s.createdAt) : null;
                    return (
                      <li key={s._id || s.createdAt} style={{ padding: '8px 4px', borderBottom: '1px solid #0b0b0e' }}>
                        <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{s.title || '(untitled)'}</div>
                        <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                          {created ? created.toLocaleString() : ''}
                          {Number.isFinite(dur) ? ` • ${formatTime(dur)}` : ''}
                        </div>
                        {audioUrl && (
                          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <a 
                              href={audioUrl} 
                              target="_blank" 
                              rel="noreferrer" 
                              style={{ 
                                color: '#60a5fa', 
                                fontSize: 12,
                                textDecoration: 'none',
                                padding: '4px 8px',
                                background: 'rgba(96, 165, 250, 0.1)',
                                borderRadius: 4,
                                border: '1px solid rgba(96, 165, 250, 0.3)'
                              }}
                            >
                              🎵 Play
                            </a>
                            <a 
                              href={audioUrl} 
                              download={`${s.title || 'stream'}.mp3`}
                              style={{ 
                                color: '#10b981', 
                                fontSize: 12,
                                textDecoration: 'none',
                                padding: '4px 8px',
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderRadius: 4,
                                border: '1px solid rgba(16, 185, 129, 0.3)'
                              }}
                            >
                              ⬇️ Download
                            </a>
                            <a 
                              href={`/amplify?sourceUrl=${encodeURIComponent(audioUrl)}`}
                              style={{ 
                                color: '#ff7a45', 
                                fontSize: 12,
                                textDecoration: 'none',
                                padding: '4px 8px',
                                background: 'rgba(255, 122, 69, 0.1)',
                                borderRadius: 4,
                                border: '1px solid rgba(255, 122, 69, 0.3)'
                              }}
                            >
                              🔊 Amplify
                            </a>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* My Uploads (embedded) */}
          <section style={{ marginTop: 24 }}>
            <h3 style={{ color: '#e5e7eb' }}>My Uploads</h3>
            {uploads.loading ? (
              <div style={{ color: '#9ca3af' }}>Loading…</div>
            ) : uploads.error ? (
              <div style={{ color: '#fca5a5' }}>{uploads.error}</div>
            ) : uploads.items.length === 0 ? (
              <div style={{ color: '#9ca3af' }}>No files uploaded yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Name</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Preview</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.items.map((f) => {
                    const href = typeof f.url === 'string' && f.url.startsWith('/uploads/') ? `${API_BASE}${f.url}` : f.url;
                    const isAudio = /\.(mp3|wav|m4a|webm|ogg|aac|flac|mp4|m4b)$/i.test(f.name || '');
                    return (
                      <tr key={f.name}>
                        <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{f.name}</td>
                        <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>
                          {isAudio && href ? (
                            <audio controls src={href} style={{ height: 28, maxWidth: 280 }} />
                          ) : (
                            <span style={{ color: '#6b7280' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {href && <a href={href} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Open</a>}
                            <button onClick={() => deleteUpload(f.name)} style={{ background: '#ef4444', color: '#fff', border: 0, borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <a href="/uploads" style={{ color: '#ff7a45' }}>Open full uploads manager →</a>
              {/* mini pagination and indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button disabled={uploads.page <= 1 || uploads.loading} onClick={async () => {
                  const page = Math.max(1, uploads.page - 1);
                  setUploads((u) => ({ ...u, loading: true }));
                  try {
                    const res = await api.listMyUploads({ page, limit: 6, sort: 'createdAt:desc' });
                    const raw = Array.isArray(res?.items) ? res.items : [];
                    const hasMore = raw.length >= 6;
                    const items = hasMore ? raw.slice(0, 5) : raw;
                    setUploads({ items, loading: false, error: '', page, hasMore });
                  } catch (e) {
                    setUploads({ items: [], loading: false, error: 'Failed to load uploads', page, hasMore: false });
                  }
                }} style={{ background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '4px 8px' }}>Prev</button>
                <span style={{ color: '#9ca3af' }}>Page {uploads.page}</span>
                <button disabled={!uploads.hasMore || uploads.loading} onClick={async () => {
                  const page = uploads.page + 1;
                  setUploads((u) => ({ ...u, loading: true }));
                  try {
                    const res = await api.listMyUploads({ page, limit: 6, sort: 'createdAt:desc' });
                    const raw = Array.isArray(res?.items) ? res.items : [];
                    const hasMore = raw.length >= 6;
                    const items = hasMore ? raw.slice(0, 5) : raw;
                    setUploads({ items, loading: false, error: '', page, hasMore });
                  } catch (e) {
                    setUploads({ items: [], loading: false, error: 'Failed to load uploads', page, hasMore: false });
                  }
                }} style={{ background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '4px 8px' }}>Next</button>
                {uploads.hasMore && <span style={{ color: '#9ca3af' }}>More files available…</span>}
              </div>
            </div>
          </section>
        </div>
      )}
      {/* Ads Analytics (admin) */}
      {adsStats.ok && (
        <section style={{ marginTop: 24 }}>
          <h3 style={{ color: '#e5e7eb' }}>Ads Analytics</h3>
          <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 12 }}>
            <div style={{ color: '#e5e7eb' }}>Total Ads: <strong>{Number(adsStats.totals.total || 0)}</strong></div>
            <div style={{ marginTop: 8 }}>
              <div style={{ color: '#9ca3af', marginBottom: 6 }}>Last 7 Days</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '6px 4px', color: '#9ca3af' }}>Day</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '6px 4px', color: '#9ca3af' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {adsStats.last7Days.map((d) => (
                    <tr key={d.day}>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827', color: '#e5e7eb' }}>{d.day}</td>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827', color: '#e5e7eb' }}>{d.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8 }}>
                <Button plain onClick={() => {
                  try {
                    const header = 'day,count\n';
                    const rows = (adsStats.last7Days || []).map(r => `${r.day},${r.count}`).join('\n');
                    const csv = header + rows + '\n';
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'ads_last7days.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch {}
                }}>Download CSV</Button>
              </div>
            </div>
          </div>
        </section>
      )}
      <GatingModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onSignup={async ()=>{ const r = await api.checkoutCredits('100'); if (r?.url) window.location.href = r.url; }}
        onLogin={()=>{ window.location.href = '/login'; }}
      />
      <Modal
        open={!!confirmState}
        title={confirmState?.title}
        onClose={() => setConfirmState(null)}
        footer={(
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setConfirmState(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => confirmState?.onConfirm?.()}>
              {confirmState?.confirmLabel || 'Confirm'}
            </Button>
          </div>
        )}
      >
        {confirmState?.message}
      </Modal>
    </div>
  );
}

function formatTime(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const cardLink = {
  display: 'block',
  background: '#111827',
  border: '1px solid #374151',
  borderRadius: 12,
  padding: 16,
  color: '#e5e7eb',
  textDecoration: 'none',
};
